import { Injectable, TemplateRef } from '@angular/core';
import { NzSafeAny } from 'ng-zorro-antd/core/types';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzNotificationComponent, NzNotificationDataOptions, NzNotificationService } from 'ng-zorro-antd/notification';

/** What a one-line message says: its icon and colour. */
export type ToastMessageKind = 'success' | 'error' | 'warning' | 'info';

@Injectable({
  providedIn: 'root'
})
export class GainsToastService {
  constructor(
    private _notification: NzNotificationService,
    private _message: NzMessageService
  ) {}

  /**
   * A one-line message at the top of the page, gone after a few seconds:
   *
   *     this._toast.message('Status refreshed', 'success');
   *
   * `success`/`error` with a title and a text show a card instead.
   */
  message(text: string, kind: ToastMessageKind = 'info', duration?: number): void {
    this._message.create(kind, text, duration === undefined ? undefined : { nzDuration: duration });
  }

  /** A one-line message with a spinner, kept until removed: its id is given to `remove`. */
  loading(text: string): string {
    return this._message.loading(text, { nzDuration: 0 }).messageId;
  }

  /** Removes the message of this id (all of them without one). */
  remove(id?: string): void {
    this._message.remove(id);
  }

  error(title: string, message: string, duration = 4000, delay?: number): void {
    if (delay) {
      setTimeout(() => {
        this._notification.error(title, message, { nzDuration: duration, nzPlacement: 'top', nzClass: 'custom-toast' });
      }, delay);
    } else {
      this._notification.error(title, message, { nzDuration: duration, nzPlacement: 'top', nzClass: 'custom-toast' });
    }
  }

  success(title: string, message: string, duration = 4000, delay?: number): void {
    if (delay) {
      setTimeout(() => {
        this._notification.success(title, message, { nzDuration: duration, nzPlacement: 'top', nzClass: 'custom-toast' });
      }, delay);
    } else {
      this._notification.success(title, message, { nzDuration: duration, nzPlacement: 'top', nzClass: 'custom-toast' });
    }
  }

  successWithButton(
    toastTemplate: TemplateRef<{
      $implicit: NzNotificationComponent;
      data: NzSafeAny;
    }>,
    title: string,
    message: string,
    duration = 4000,
    delay?: number
  ) {
    const toastData = { title, message };

    if (delay) {
      setTimeout(() => {
        this._notification.template(toastTemplate!, { nzData: toastData, nzDuration: duration, nzPlacement: 'top', nzClass: 'custom-toast' });
      }, delay);
    } else {
      this._notification.template(toastTemplate!, { nzData: toastData, nzDuration: duration, nzPlacement: 'top', nzClass: 'custom-toast' });
    }
  }
}
